import { ChangeDetectorRef, Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { FilesizePipe } from '../filesize.pipe'
import { MatTooltip } from '@angular/material/tooltip'
import { MatIcon } from '@angular/material/icon'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { catchError, of, Subscription } from 'rxjs'
import { FileUploadType, getUploadErrorMessage, UploadsService } from '../uploads.service'
import { ToastService } from '../toast.service'
import { LoggerService } from '../logger.service'
import { MatButton, MatIconButton } from '@angular/material/button'
import { DocumentUploadInfo, getFileExtension, getFileName } from '../utils'
import { FileValidationService } from '../file-validation.service'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { DurationPipe } from '../duration.pipe'
import { PdfPageCountService } from '../pdf-page-count.service'

export type PageLimitExceededEvent = {
  pageCount: number
  maxPages: number
  fileName: string
}

export type FileSizeLimitExceededEvent = {
  fileSize: number
  maxFileSize: number
  fileName: string
}

const MAX_AGENDA_PAGES = 10
@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [FilesizePipe, MatTooltip, MatIcon, TranslatePipe, TranslateDirective, MatIconButton, MatButton, MatProgressBarModule, DurationPipe],
  templateUrl: './file-upload.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './file-upload.component.scss',
})
export class FileUploadComponent {
  @Input({ required: true })
  acceptedFileTypes!: string[]

  @Input()
  maxSize: number | null = null

  @Input()
  maxPages: number | null = null

  @Input()
  maxPagesErrorMessage = ''

  /**
   * Like maxSize, but surfaces the failure inline below the file name and emits
   * fileSizeLimitExceeded (rather than a toast) so consumers can report it.
   */
  @Input()
  maxFileSizeBytes: number | null = null

  @Input()
  maxFileSizeErrorMessage = ''

  @Input({ required: true })
  type!: FileUploadType

  @Input()
  uploadFailedMessage = 'CALLS.CALL_INFO.DOCUMENT_UPLOAD.UPLOAD_FAILED'

  @Output()
  fileChange = new EventEmitter<DocumentUploadInfo | null>()

  @Output()
  pageLimitExceeded = new EventEmitter<PageLimitExceededEvent>()

  @Output()
  fileSizeLimitExceeded = new EventEmitter<FileSizeLimitExceededEvent>()

  @Output()
  progressChange = new EventEmitter<{ progress: number; remainingTimeSeconds: number | null }>()

  @ViewChild('fileInput')
  fileInputElement: ElementRef<HTMLInputElement> | undefined

  private changeDetectorRef = inject(ChangeDetectorRef)
  uploadsService = inject(UploadsService)
  toastService = inject(ToastService)
  loggerService = inject(LoggerService)
  fileValidationService = inject(FileValidationService)
  pdfPageCountService = inject(PdfPageCountService)
  uploadSubscription: Subscription | null = null
  chosenFile: File | null = null
  isUploading = false
  isScanning = false
  isCheckingPages = false
  // Translation key of the limit error shown below the file name, or null when none.
  limitErrorMessage: string | null = null
  uploadProgress = 0
  remainingTimeSeconds: number | null = null

  async fileInputChanged(fileInputEvent: any) {
    const chosenFile = fileInputEvent.target.files[0] ?? null
    if (chosenFile === null) {
      return
    }
    const fileIsValid = this.fileValidationService.validateFile(chosenFile, this.acceptedFileTypes, this.maxSize)
    if (!fileIsValid) {
      return
    }

    this.chosenFile = chosenFile
    this.limitErrorMessage = null
    const extension = getFileExtension(chosenFile)

    // Check the cheap, synchronous size limit before the (expensive) page count.
    if (this.maxFileSizeBytes !== null && chosenFile.size > this.maxFileSizeBytes) {
      this.fileSizeLimitExceeded.emit({ fileSize: chosenFile.size, maxFileSize: this.maxFileSizeBytes, fileName: getFileName(chosenFile) })
      this.rejectFile(this.maxFileSizeErrorMessage)
      return
    }

    if (this.maxPages !== null && extension.toLowerCase() === 'pdf') {
      const withinPageLimit = await this.checkPageLimit(chosenFile, this.maxPages)
      if (!withinPageLimit) {
        return
      }
    }

    this.startUpload(chosenFile, extension)
  }

  /** Shows the given limit error below the file name and blocks submission. */
  private rejectFile(errorMessage: string): void {
    this.limitErrorMessage = errorMessage
    this.fileChange.emit(null)
  }

  /**
   * Counts the pages of the chosen PDF and, if it exceeds `maxPages`, shows the
   * error, blocks the upload and notifies the parent so it can disable submission
   * (and report the rejection). Returns true when the upload may proceed.
   */
  private async checkPageLimit(file: File, maxPages: number): Promise<boolean> {
    this.isCheckingPages = true
    this.changeDetectorRef.detectChanges()
    try {
      const pageCount = await this.pdfPageCountService.getPageCount(file)
      if (pageCount > maxPages) {
        this.pageLimitExceeded.emit({ pageCount, maxPages, fileName: getFileName(file) })
        this.rejectFile(this.maxPagesErrorMessage)
        return false
      }
      return true
    } catch (error) {
      // If we can't read the page count locally, let the upload proceed and rely
      // on server-side validation rather than blocking the user.
      this.loggerService.error('Failed to read PDF page count', error)
      return true
    } finally {
      this.isCheckingPages = false
      this.changeDetectorRef.detectChanges()
    }
  }

  private startUpload(chosenFile: File, extension: string) {
    this.isUploading = true
    this.uploadProgress = 0
    this.remainingTimeSeconds = null
    const fileName = getFileName(chosenFile)
    const upload$ = this.type === 'recording' ? this.uploadsService.uploadRecording(chosenFile) : this.uploadsService.upload(this.type, chosenFile)
    this.uploadSubscription = upload$
      .pipe(
        catchError((error) => {
          const errorMessage = getUploadErrorMessage(error) ?? this.uploadFailedMessage
          if (this.type === 'agenda') {
            this.toastService.show(errorMessage, { maxPages: MAX_AGENDA_PAGES })
          } else {
            this.toastService.show(errorMessage)
          }
          this.loggerService.error('Error uploading file', error)
          this.isUploading = false
          this.isScanning = false
          this.uploadProgress = 0
          this.remainingTimeSeconds = null
          this.chosenFile = null
          this.changeDetectorRef.detectChanges()
          return of(null)
        })
      )
      .subscribe((uploadInfo) => {
        if (uploadInfo !== null) {
          this.uploadProgress = uploadInfo.progress
          this.remainingTimeSeconds = uploadInfo.remainingTimeSeconds
          this.isScanning = uploadInfo.isScanning
          this.progressChange.emit({ progress: this.uploadProgress, remainingTimeSeconds: this.remainingTimeSeconds })
          if (uploadInfo.uploadKey !== null) {
            this.fileChange.emit({ key: uploadInfo.uploadKey, extension: extension.toUpperCase(), fileName })
            this.isUploading = false
            this.isScanning = false
            this.uploadProgress = 0
            this.remainingTimeSeconds = null
          }
          this.changeDetectorRef.detectChanges()
        }
      })
  }

  removeFile() {
    this.chosenFile = null
    this.isUploading = false
    this.isCheckingPages = false
    this.limitErrorMessage = null
    this.uploadProgress = 0
    this.remainingTimeSeconds = null
    this.isScanning = false
    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe()
      this.uploadSubscription = null
    }
    this.fileChange.emit(null)
  }

  get acceptedFileTypesString() {
    return this.acceptedFileTypes.map((fileType) => `.${fileType}`).join(',')
  }

  openFileDialog(): void {
    if (this.fileInputElement !== undefined) {
      this.fileInputElement.nativeElement.click()
    }
  }
}
